import { AspectRatio, Box, Flex, Icon, Text } from "@chakra-ui/react";
import React from "react";
import { HiOutlineCheck, HiOutlineX } from "react-icons/hi";

import colors from "../../../../theme/colors";
import { getQuestionScoreColor } from "./scoring";

type ScoreIconProps = {
  score: number; // 0-4
  qualifies?: boolean | null; // For qualification questions
  customWeight?: number | null;
  redacted?: boolean; // When true, render redacted badge instead of score
  scoringQuestionsCount?: number; // For default weight when customWeight not provided
};

const CARD_WIDTH = 64;

type BottomVariant = "redacted" | "weight" | "label" | "not_scored";

const BottomContent: React.FC<{
  variant: BottomVariant;
  color?: string;
  percentText?: string | null;
  label?: string;
}> = ({ variant, color, percentText, label }) => {
  if (variant === "redacted") {
    return (
      <Text
        fontSize="xs"
        lineHeight="1.3"
        color={color || colors.yellow[700]}
        textAlign="center"
      >
        Redacted response
      </Text>
    );
  }
  if (variant === "weight") {
    return (
      <>
        <Text fontSize="xs" color="gray.600">
          Weight
        </Text>
        <Text fontSize="xs" color="gray.600">
          {percentText}
        </Text>
      </>
    );
  }
  if (variant === "label") {
    return (
      <Text fontSize="xs" color="gray.600" textAlign="center">
        {label}
      </Text>
    );
  }
  return (
    <Text fontSize="xs" lineHeight="1.3" color="gray.600" textAlign="center">
      Not scored
    </Text>
  );
};

const ScoreIcon: React.FC<ScoreIconProps> = ({
  score,
  qualifies,
  customWeight,
  redacted,
  scoringQuestionsCount,
}) => {
  // Core card renderer: 1:1 top area + optional bottom
  const renderCard = (options: {
    cardBg: string;
    cardBorderColor: string;
    topBg: string;
    topContent: React.ReactNode;
    dividerColor?: string;
    bottomContent?: React.ReactNode;
  }): JSX.Element => {
    const {
      cardBg,
      cardBorderColor,
      topBg,
      topContent,
      dividerColor,
      bottomContent,
    } = options;
    return (
      <Box
        borderRadius="lg"
        borderWidth="1px"
        borderColor={cardBorderColor}
        bg={cardBg}
        width={`${CARD_WIDTH}px`}
        overflow="hidden"
        flexShrink={0}
      >
        <AspectRatio ratio={1} w="100%">
          <Flex align="center" justify="center" bg={topBg}>
            {topContent}
          </Flex>
        </AspectRatio>
        {bottomContent && (
          <>
            <Box
              w="100%"
              h="1px"
              bg={dividerColor || cardBorderColor}
              opacity={0.6}
            />
            <Flex
              align="center"
              justify="center"
              height="48px"
              px="3"
              direction="column"
            >
              {bottomContent}
            </Flex>
          </>
        )}
      </Box>
    );
  };

  // Redacted
  if (redacted) {
    return renderCard({
      cardBg: colors.yellow[100],
      cardBorderColor: colors.yellow[700],
      topBg: colors.yellow[100],
      topContent: (
        <Text
          fontWeight="bold"
          fontSize="3xl"
          lineHeight="1"
          color={colors.yellow[700]}
        >
          NS
        </Text>
      ),
      dividerColor: colors.yellow[700],
      bottomContent: (
        <BottomContent variant="redacted" color={colors.yellow[700]} />
      ),
    });
  }

  // Qualifier variants
  if (qualifies === true) {
    return renderCard({
      cardBg: "white",
      cardBorderColor: "gray.200",
      topBg: getQuestionScoreColor(4),
      topContent: (
        <Icon as={HiOutlineCheck} color="white" boxSize="8" strokeWidth={3} />
      ),
      bottomContent: <BottomContent variant="label" label="Qualified" />,
    });
  }
  if (qualifies === false) {
    return renderCard({
      cardBg: "white",
      cardBorderColor: "gray.200",
      topBg: getQuestionScoreColor(1),
      topContent: (
        <Icon as={HiOutlineX} color="white" boxSize="8" strokeWidth={3} />
      ),
      bottomContent: <BottomContent variant="label" label="Disqual." />,
    });
  }

  // Score only and score with weight use same renderer; bottom is optional
  const topContent = (
    <Text color="white" fontSize="2xl" fontWeight="bold">
      {score > 0 ? score.toFixed(1) : "NS"}
    </Text>
  );

  if (customWeight) {
    return renderCard({
      cardBg: "white",
      cardBorderColor: "gray.200",
      topBg: getQuestionScoreColor(score),
      topContent,
      bottomContent: (
        <BottomContent variant="weight" percentText={`${customWeight}%`} />
      ),
    });
  }

  const isScored = !!score && score > 0;
  const derivedWeight =
    isScored && scoringQuestionsCount && scoringQuestionsCount > 0
      ? 100 / scoringQuestionsCount
      : null;
  const derivedWeightDisplay =
    derivedWeight == null
      ? null
      : Math.abs(derivedWeight - Math.round(derivedWeight)) < 0.01
      ? `${Math.round(derivedWeight)}`
      : `${derivedWeight.toFixed(1)}`;

  return renderCard({
    cardBg: "white",
    cardBorderColor: "gray.200",
    topBg: getQuestionScoreColor(score),
    topContent,
    bottomContent:
      isScored && derivedWeightDisplay ? (
        <BottomContent
          variant="weight"
          percentText={`${derivedWeightDisplay}%`}
        />
      ) : (
        <BottomContent variant="not_scored" />
      ),
  });
};

export default ScoreIcon;
